/*
 * The estimate a work order or a recurring schedule carries (AB#1618): how long the job is
 * expected to take, in hours. A plan, not a record — the labour log is the record.
 *
 * Shared because two forms enter it, the work order's and the schedule's, and the week overview
 * sums it. The rules are a copy of the server's CmmsEstimatedHours, which is the authority. The
 * copy exists so a typo is pointed out under the field rather than arriving as a toast carrying
 * an English sentence about parameter names.
 */
import cmmsMessages from "./cmmsMessages";

// The server's ceiling. A typo guard, not a policy: nobody plans one job at 10 000 hours.
export const ESTIMATED_HOURS_MAX = 1000;

/*
 * Digits with at most one decimal separator, either kind.
 *
 * The comma because Swedish keyboards type "2,5" and Number("2,5") is NaN. Stricter than Number()
 * otherwise, on purpose: Number() reads "1e3" as 1000 and "0x10" as 16, neither of which is what
 * anyone typing an hour count meant. The minus is let through only so "-1" is answered with the
 * range rather than with "not a number".
 */
const HOURS_PATTERN = /^-?(?:\d+(?:[.,]\d*)?|[.,]\d+)$/;

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";

/*
 * What the box holds, as the number to send.
 *
 * null for an empty box: no estimate, which is not zero. Zero is someone saying the job takes no
 * time; empty is nobody having said, and the week overview counts those apart rather than adding
 * them in as nothing. NaN for anything that is not an hour count, so neither can be mistaken for
 * the other. A number passes through, which is what a loaded, untouched field holds.
 */
export const parseEstimatedHours = (value) => {
  if (isBlank(value)) return null;
  const trimmed = String(value).trim();
  if (!HOURS_PATTERN.test(trimmed)) return NaN;
  return Number(trimmed.replace(",", "."));
};

/*
 * The message to show under the field, or null when the entry is fine. Blank is fine.
 *
 * More decimals than two are fine too: the server rounds to two and sends back what it kept.
 */
export const validateEstimatedHours = (value, intl) => {
  const hours = parseEstimatedHours(value);
  if (hours === null) return null;
  if (Number.isNaN(hours)) return intl.formatMessage(cmmsMessages.estimatedHoursInvalid);
  if (hours < 0 || hours > ESTIMATED_HOURS_MAX) {
    return intl.formatMessage(cmmsMessages.estimatedHoursRange, { max: ESTIMATED_HOURS_MAX });
  }
  return null;
};

/*
 * What the box shows.
 *
 * The forms seed it with the saved number and replace that with the typed string on the first
 * keystroke, so this sees both. A string is shown as typed — reformatting it mid-entry would eat
 * the trailing comma of "2," before the 5 arrives. A number is shown in the reader's notation,
 * "2,5" in Swedish and "2.5" in English, both of which parseEstimatedHours reads back. No digit
 * grouping, so 1000 does not come back as "1 000", which the pattern would refuse.
 *
 * Formatting here rather than in the load effect keeps intl out of that effect's dependencies,
 * where a language switch would reset a half-edited form.
 */
export const formatEstimatedHoursInput = (intl, value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (Number.isNaN(Number(value))) return "";
  return intl.formatNumber(Number(value), { maximumFractionDigits: 2, useGrouping: false });
};
